import { loadConfig } from '../../../../utils/configLoader';
import { getPais } from '../../helper';

/**
 * Flag runtime `auto_via_bff` (react_config.json).
 *
 * Con el flag apagado (o ausente) el cotizador de auto/moto usa exactamente el
 * camino viejo contra el monolito. Con el flag prendido usa la capa nueva
 * contra el BFF (`src/Services/API/bff/auto/*`).
 *
 * El BFF es Argentina-only: en UY/BR el flag se ignora.
 */

/** Marca de sesión: el bootstrap del BFF falló, esta sesión se queda en legacy. */
const CLAVE_LEGACY = 'auto_via_bff_legacy';

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/**
 * Degrada la sesión al camino viejo. Se llama cuando `GET parametros` del BFF
 * falla con el flag prendido (deliverable 10): no tiene sentido seguir pidiendo
 * catálogos y precios a un backend que no responde.
 */
export const marcarSesionLegacy = (motivo: string): void => {
  // eslint-disable-next-line no-console
  console.warn(
    `[auto_via_bff] El BFF no respondió el bootstrap (${motivo}). ` +
      'La sesión sigue por el camino viejo (monolito).'
  );
  storage()?.setItem(CLAVE_LEGACY, '1');
};

/** true si esta sesión ya se degradó al camino viejo. */
export const esSesionLegacy = (): boolean =>
  storage()?.getItem(CLAVE_LEGACY) === '1';

/** Solo para tests: limpia la marca de sesión legacy. */
export const limpiarSesionLegacy = (): void => {
  storage()?.removeItem(CLAVE_LEGACY);
};

/**
 * Lee el flag del config runtime. Default false si falta la clave, si el
 * config no carga, si el país no es AR o si la sesión se degradó a legacy.
 */
export const isAutoViaBff = async (): Promise<boolean> => {
  if (esSesionLegacy()) return false;
  if (getPais() !== 'AR') return false;
  try {
    const config = await loadConfig();
    return config?.auto_via_bff === true;
  } catch {
    return false;
  }
};

/**
 * Versión sincrónica, para componentes que ya tienen el config en mano
 * (`useConfig().config`). Misma semántica que `isAutoViaBff`.
 */
export const autoViaBffDesdeConfig = (
  config?: { auto_via_bff?: boolean; pais?: string } | null
): boolean => {
  if (esSesionLegacy()) return false;
  if (getPais() !== 'AR') return false;
  return config?.auto_via_bff === true;
};
